import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { userService } from '@/services/user_service'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { emailSchema, optionalEmailSchema } from '@/lib/validations'
import { User } from '@magnetar/admin-api-interfaces'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { logger } from '@magnetar/utils'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { useAuth } from '@/hooks/useAuth'
import { useViewMode } from '@/stores/viewMode'
import { useUserRoles } from '@/hooks/useUserRoles'
import { useMyPermissions } from '@/hooks/useMyPermissions'
import { AllowedAction } from '@/data/allowedActions'
import { PasswordResetSection } from '@/components/PasswordResetSection'

// 建立 schema 的函數，根據是否為編輯模式調整驗證規則
// 新增模式：email 必填
// 編輯模式：email 選填
const createFormSchema = (isEditMode: boolean) => {
  if (isEditMode) {
    // 編輯模式
    return z.object({
      user_name: z.string().min(1, { message: '使用者名稱為必填' }),
      account: z.string().min(1, { message: '帳號為必填' }),
      email: optionalEmailSchema(),
      is_active: z.boolean(),
      user_role_id: z.string().min(1, { message: '請選擇角色' }),
    })
  }

  // 新增模式：email 必填
  return z.object({
    user_name: z.string().min(1, { message: '使用者名稱為必填' }),
    account: z.string().min(1, { message: '帳號為必填' }),
    email: emailSchema(),
    is_active: z.boolean(),
    user_role_id: z.string().min(1, { message: '請選擇角色' }),
  })
}

type FormSchema = z.infer<ReturnType<typeof createFormSchema>>

interface UserSettingDialogProps {
  user?: User
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

export function UserSettingDialog({ user, open, onOpenChange, onSuccess }: UserSettingDialogProps) {
  const { token } = useAuth()
  const { mode, id } = useViewMode()
  const { hasPermission } = useMyPermissions()
  const isEditMode = !!user
  const isProviderMode = mode === 'provider'
  const [pendingPassword, setPendingPassword] = useState<string | undefined>(undefined)

  // 檢查是否有 user_management 權限
  // sys_admin: 檢查 'system_user' 權限的 U 動作
  // provider: 檢查 'provider_user_management' 權限的 U 動作
  const canResetPassword = isEditMode && (
    hasPermission('system_user', AllowedAction.U) ||
    hasPermission('provider_user_management', AllowedAction.U)
  )

  // 根據 mode 決定要取得哪個層級的角色
  // sys_admin mode: 取得 sys_admin 層級的角色
  // provider mode: 取得該 provider 的角色
  const { userRoles, isLoading: isLoadingRoles } = useUserRoles({
    pageSize: 100,
    scopeType: isProviderMode ? 'provider' : 'sys_admin',
    providerId: isProviderMode && id ? id : undefined,
  })

  const form = useForm<FormSchema>({
    resolver: zodResolver(createFormSchema(isEditMode)),
    defaultValues: {
      user_name: '',
      account: '',
      email: '',
      is_active: true,
      user_role_id: '',
    },
  })

  useEffect(() => {
    if (user) {
      form.reset({
        user_name: user.user_name,
        account: user.account,
        email: user.email ?? '',
        is_active: user.is_active,
        user_role_id: user.user_role_id?.toString() ?? '',
      })
    } else {
      form.reset({
        user_name: '',
        account: '',
        email: '',
        is_active: true,
        user_role_id: '',
      })
    }
  }, [user, form])

  const onSubmit = async (values: FormSchema) => {
    try {
      const data: {
        user_name: string
        account: string
        email?: string
        is_active: boolean
        provider_id?: number
        user_role_id?: number
        password?: string
      } = {
        user_name: values.user_name,
        account: values.account,
        email: values.email || undefined,
        is_active: values.is_active,
      }

      // 如果有設定新密碼，加入請求
      if (pendingPassword) {
        data.password = pendingPassword
      }

      // 如果有選擇角色，傳入 user_role_id
      if (values.user_role_id) {
        data.user_role_id = parseInt(values.user_role_id, 10)
      }

      // 如果在 provider mode 下，傳入 provider_id
      if (isProviderMode && id) {
        data.provider_id = id
      }

      const response = user
        ? await userService.updateUser(user.user_id, data, token)
        : await userService.createUser(data, token)

      if (!response.success) {
        throw new Error(response.error?.message || '儲存失敗')
      }

      onOpenChange(false)
      form.reset()
      onSuccess()
    } catch (error) {
      logger.error('儲存使用者失敗:', error)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {user
              ? isProviderMode
                ? '編輯組織用戶'
                : '編輯使用者'
              : isProviderMode
                ? '新增組織用戶'
                : '新增使用者'}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="user_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>使用者名稱</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="account"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>帳號</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="example@domain.com"
                      disabled={isEditMode && !!user?.email}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="user_role_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>角色</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={isLoadingRoles ? '載入中...' : '請選擇角色'} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {userRoles.map((role) => (
                        <SelectItem key={role.user_role_id} value={role.user_role_id.toString()}>
                          {role.role_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">啟用狀態</FormLabel>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
            {isEditMode && (
              <PasswordResetSection
                canResetPassword={canResetPassword}
                onPasswordChange={setPendingPassword}
              />
            )}
            <DialogFooter>
              <Button type="submit">{user ? '儲存' : '新增'}</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
